/**
 * EU cookie / Consent Mode helpers.
 * Necessary cookies always on; analytics & marketing require opt-in.
 */

export const COOKIE_CONSENT_KEY = 'coursolo_cookie_consent';
export const COOKIE_CONSENT_LOG_KEY = 'coursolo_cookie_consent_log';
/** Bump when policy/categories change so users are asked again. */
export const COOKIE_CONSENT_VERSION = 2;
export const OPEN_COOKIE_SETTINGS_EVENT = 'coursolo:open-cookie-settings';
export const CONSENT_CHANGED_EVENT = 'coursolo:cookie-consent-changed';

export const GA_MEASUREMENT_ID = 'G-47EVT7JTZX';
export const GOOGLE_ADS_ID = 'AW-18421133362';

export function defaultConsent() {
  return {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    analytics: false,
    marketing: false,
    updatedAt: null,
  };
}

export function readCookieConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Number(parsed.version) !== COOKIE_CONSENT_VERSION) return null;
    return {
      ...defaultConsent(),
      ...parsed,
      necessary: true,
    };
  } catch {
    return null;
  }
}

export function hasAnsweredCookieConsent() {
  return Boolean(readCookieConsent()?.updatedAt);
}

export function hasAnalyticsConsent() {
  return Boolean(readCookieConsent()?.analytics);
}

export function hasMarketingConsent() {
  return Boolean(readCookieConsent()?.marketing);
}

/** Third-party media (YouTube) — allowed with marketing consent or explicit click-load. */
export function hasMediaCookieConsent() {
  const c = readCookieConsent();
  return Boolean(c?.marketing || c?.analytics);
}

export function applyGtagConsent({ analytics = false, marketing = false } = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
}

function appendConsentLog(entry) {
  try {
    const prev = JSON.parse(localStorage.getItem(COOKIE_CONSENT_LOG_KEY) || '[]');
    const next = Array.isArray(prev) ? prev.slice(-19) : [];
    next.push(entry);
    localStorage.setItem(COOKIE_CONSENT_LOG_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

export function readConsentLog() {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_LOG_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveCookieConsent({ analytics = false, marketing = false } = {}) {
  const next = {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    analytics: Boolean(analytics),
    marketing: Boolean(marketing),
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
  appendConsentLog({ ...next, source: 'banner' });
  applyGtagConsent(next);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: next }));
  }
  return next;
}

export function openCookieSettings() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}

export function gtagEvent(eventName, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return false;
  window.gtag('event', eventName, params);
  return true;
}

/** GA4 page_view for SPA navigations — only with analytics consent. */
export function trackSpaPageView(pathname) {
  if (!hasAnalyticsConsent()) return false;
  return gtagEvent('page_view', {
    page_path: pathname || (typeof window !== 'undefined' ? window.location.pathname : '/'),
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
    send_to: GA_MEASUREMENT_ID,
  });
}

/**
 * Sign-up conversion — GA4 + optional Ads label.
 * Fires analytics event with analytics consent; Ads conversion only with marketing consent.
 */
export function trackSignUpConversion() {
  if (hasAnalyticsConsent()) {
    gtagEvent('sign_up', { method: 'email' });
  }
  if (!hasMarketingConsent()) return false;
  const label = process.env.REACT_APP_GOOGLE_ADS_SIGNUP_LABEL;
  if (label) {
    return gtagEvent('conversion', { send_to: `${GOOGLE_ADS_ID}/${label}` });
  }
  return gtagEvent('conversion', { send_to: GOOGLE_ADS_ID });
}

/** Purchase / subscription conversion — gated on marketing (Ads) + analytics value. */
export function trackPurchaseConversion({ value, currency = 'EUR', transactionId } = {}) {
  if (hasAnalyticsConsent()) {
    gtagEvent('purchase', {
      value: value != null ? Number(value) : undefined,
      currency,
      transaction_id: transactionId,
    });
  }
  if (!hasMarketingConsent()) return false;
  const label = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL;
  const payload = {
    send_to: label ? `${GOOGLE_ADS_ID}/${label}` : GOOGLE_ADS_ID,
    value: value != null ? Number(value) : undefined,
    currency,
    transaction_id: transactionId,
  };
  return gtagEvent('conversion', payload);
}
